import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Circle, Wallet, User, Users, Gift, ExternalLink, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || window.location.origin;

const FirstStepsModal = ({ user, onClose, onStepComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [walletAddress, setWalletAddress] = useState('');

  // Check which steps are already completed
  useEffect(() => {
    const completed = [];
    if (user?.full_name) completed.push(0); // Profile complete
    if (user?.tron_wallet_address) {
      completed.push(1); // Wallet set
      setWalletAddress(user.tron_wallet_address);
    }
    // Add logic to check referrals later
    setCompletedSteps(completed);
    
    // Set current step to first incomplete step
    if (completed.length < 3) {
      for (let i = 0; i < 3; i++) {
        if (!completed.includes(i)) {
          setCurrentStep(i);
          break;
        }
      }
    }
  }, [user]);

  const steps = [
    {
      id: 0,
      title: "Complete Your Profile",
      description: "Make sure your name and details are set up correctly",
      icon: User,
      completed: completedSteps.includes(0),
      action: "profile"
    },
    {
      id: 1,
      title: "Add Your Tron Wallet Address", 
      description: "Required to receive commissions and payments",
      icon: Wallet,
      completed: completedSteps.includes(1),
      action: "wallet",
      critical: true
    },
    {
      id: 2,
      title: "Start Paying It Forward",
      description: "Use your first earnings to sponsor others and unlock Phase 2",
      icon: Users,
      completed: completedSteps.includes(2),
      action: "referral"
    }
  ];

  const handleStepComplete = (stepId) => {
    if (!completedSteps.includes(stepId)) {
      const newCompleted = [...completedSteps, stepId];
      setCompletedSteps(newCompleted);
      onStepComplete?.(stepId);
      
      // Move to next step
      const nextStep = steps.find(step => !newCompleted.includes(step.id));
      if (nextStep) {
        setCurrentStep(nextStep.id);
      }
    }
  };

  const handleWalletSave = async () => {
    if (!walletAddress || walletAddress.length < 10) {
      toast.error('Please enter a valid Tron wallet address');
      return;
    }

    try {
      // In a real implementation, this would save to backend
      toast.success('Wallet address saved successfully!');
      handleStepComplete(1);
    } catch (error) {
      toast.error('Failed to save wallet address');
    }
  };

  const currentStepData = steps[currentStep];
  const allCompleted = completedSteps.length === steps.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Welcome to BBC Quick List! 🎉
              </h2>
              <p className="text-gray-600 mt-1">
                Let's get you set up to start earning commissions
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
              <span>Setup Progress</span>
              <span>{completedSteps.length}/3 Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(completedSteps.length / 3) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Steps Overview */}
          <div className="space-y-4 mb-8">
            {steps.map((step) => {
              const StepIcon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = step.completed;
              
              return (
                <div 
                  key={step.id}
                  className={`flex items-start space-x-4 p-4 rounded-lg border-2 transition-all ${
                    isActive 
                      ? 'border-yellow-400 bg-yellow-50' 
                      : isCompleted 
                        ? 'border-green-200 bg-green-50' 
                        : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    isCompleted 
                      ? 'bg-green-500 text-white' 
                      : isActive 
                        ? 'bg-yellow-400 text-black' 
                        : 'bg-gray-300 text-gray-600'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="h-6 w-6" />
                    ) : (
                      <StepIcon className="h-6 w-6" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                        {step.title}
                      </h3>
                      {step.critical && (
                        <Badge variant="destructive" className="text-xs">
                          Required
                        </Badge>
                      )}
                      {isCompleted && (
                        <Badge variant="default" className="text-xs bg-green-100 text-green-800">
                          Complete
                        </Badge>
                      )}
                    </div>
                    <p className={`text-sm ${isActive ? 'text-gray-600' : 'text-gray-500'} mt-1`}>
                      {step.description}
                    </p>
                  </div>
                  
                  {isActive && !isCompleted && (
                    <ArrowRight className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-2" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Current Step Detail */}
          {!allCompleted && (
            <Card className="border-2 border-yellow-400">
              <CardHeader className="bg-yellow-50">
                <CardTitle className="flex items-center gap-2">
                  {React.createElement(currentStepData.icon, { className: "h-5 w-5 text-yellow-600" })}
                  Step {currentStep + 1}: {currentStepData.title}
                </CardTitle>
                <CardDescription>
                  {currentStepData.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Step-specific content */}
                {currentStep === 0 && (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                      ✅ Your profile looks good! Your name: <strong>{user?.full_name}</strong>
                    </p>
                    <Button 
                      onClick={() => handleStepComplete(0)}
                      className="btn-primary w-full"
                    >
                      Profile Complete - Continue
                    </Button>
                  </div>
                )}

                {currentStep === 1 && (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Wallet className="h-5 w-5 text-red-500 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-red-800 mb-2">
                            ⚠️ Critical: Add Your Tron Wallet Address
                          </p>
                          <p className="text-red-700">
                            If you don't have a Tron receive address, all your earnings will go to someone else 
                            until you enter and save a wallet that your earnings can go to.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Tron Wallet Address
                      </label>
                      <input
                        type="text"
                        value={walletAddress}
                        onChange={(e) => setWalletAddress(e.target.value)}
                        placeholder="Enter your Tron wallet address (e.g., TR7NHqj...)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-800 mb-3">
                        Don't have a Tron wallet? Get one here:
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="justify-start"
                          onClick={() => window.open('https://nowpayments.io', '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          NOW Payments
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="justify-start"
                          onClick={() => window.open('https://changenow.io', '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          ChangeNOW
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="justify-start"
                          onClick={() => window.open('https://trustwallet.com', '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Trust Wallet
                        </Button>
                      </div>
                    </div>

                    <Button 
                      onClick={handleWalletSave}
                      className="btn-primary w-full"
                      disabled={!walletAddress}
                    >
                      Save Wallet Address & Continue
                    </Button>
                  </div>
                )}

                {currentStep === 2 && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Gift className="h-5 w-5 text-blue-500 mt-0.5" />
                        <div className="text-sm">
                          <p className="font-medium text-blue-800 mb-2">
                            💡 Start Paying It Forward
                          </p>
                          <p className="text-blue-700">
                            If you do not have any money, use your first earnings to begin paying it forward. 
                            After 4 payments received, you must sponsor someone to continue earning commissions.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <p className="text-gray-600 mb-4">
                        Use your referral link to invite others and start earning:
                      </p>
                      <div className="bg-gray-100 p-3 rounded-lg font-mono text-sm break-all">
                        {`${PUBLIC_URL}/?ref=${user?.id}`}
                      </div>
                    </div>

                    <Button 
                      onClick={() => handleStepComplete(2)}
                      className="btn-primary w-full"
                    >
                      I Understand - Let's Go!
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Completion State */}
          {allCompleted && (
            <Card className="border-2 border-green-400">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  🎉 Setup Complete!
                </CardTitle>
                <CardDescription className="text-green-700">
                  You're all set up and ready to start earning with BBC Quick List.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <p className="text-gray-600">
                    Welcome to Phase 1! You can now:
                  </p>
                  <ul className="text-left text-sm text-gray-600 space-y-1">
                    <li>✅ Receive commissions to your Tron wallet</li>
                    <li>✅ Access your referral tools and lead capture pages</li>
                    <li>✅ Start building your network</li>
                    <li>✅ Track your progress toward Phase 2 (12 referrals)</li>
                  </ul>
                  <Button 
                    onClick={onClose}
                    className="btn-primary w-full"
                  >
                    Start Building My Network!
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default FirstStepsModal;